import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaImage, FaPaperPlane, FaPoll, FaTrash } from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebSegments,
  uploadWhatsAppWebMedia,
  createWhatsAppWebCampaign,
} from "../../../../services/api";

/**
 * Quick Sender — disparo de campanha simples (texto ou imagem).
 * Replica o fluxo do OrbitSender: compor → escolher segmento → "Quando enviar?".
 */
export default function QuickSenderPage() {
  const { user } = useAuth();
  const [segments, setSegments] = useState([]);
  const [segmentId, setSegmentId] = useState("");

  const [message, setMessage] = useState("");
  const [mentionAll, setMentionAll] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [showSchedule, setShowSchedule] = useState(false);
  const [whenMode, setWhenMode] = useState("now"); // "now" | "schedule"
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");

  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Poll modal (estilo OrbitSender)
  const [showPoll, setShowPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [pollAllowMulti, setPollAllowMulti] = useState(false);
  const [poll, setPoll] = useState(null);

  useEffect(() => {
    listWhatsAppWebSegments().then((res) => {
      const items = res?.data || [];
      setSegments(items);
      if (items.length === 1) setSegmentId(items[0].id);
    });
  }, []);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  function handlePickImage(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  }

  function clearImage() {
    setImageFile(null);
    setImagePreview(null);
  }

  function savePoll() {
    const cleanOpts = pollOptions.map((o) => o.trim()).filter(Boolean);
    if (!pollQuestion.trim() || cleanOpts.length < 2) {
      alert("Preencha pergunta + ao menos 2 opções.");
      return;
    }
    setPoll({
      question: pollQuestion.trim(),
      options: cleanOpts,
      allowMultipleAnswers: pollAllowMulti,
    });
    setShowPoll(false);
  }

  function clearPoll() {
    setPoll(null);
    setPollQuestion("");
    setPollOptions(["", ""]);
    setPollAllowMulti(false);
  }

  function buildScheduledAt() {
    if (whenMode !== "schedule" || !scheduleDate || !scheduleTime) return null;
    return `${scheduleDate}T${scheduleTime}:00`;
  }

  async function handleSend() {
    if (!segmentId) {
      alert("Selecione um segmento.");
      return;
    }
    if (!poll && !message.trim() && !imageFile) {
      alert("Escreva uma mensagem, anexe imagem ou crie uma enquete.");
      return;
    }

    setBusy(true);
    setFeedback(null);

    try {
      let mediaUrl = null;
      if (imageFile) {
        const up = await uploadWhatsAppWebMedia(imageFile);
        mediaUrl = up?.data?.url;
      }

      const messageType = poll ? "poll" : imageFile ? "image" : "text";
      const payload = {
        name: `Quick Campaign ${new Date().toLocaleString("pt-BR")}`,
        message_type: messageType,
        segment_id: segmentId,
        mention_all: mentionAll,
        scheduled_at: buildScheduledAt(),
      };
      if (poll) {
        payload.message = poll.question;
        payload.poll_data = poll;
      } else {
        payload.message = message.trim();
      }
      if (imageFile && mediaUrl) {
        payload.media_path = mediaUrl;
        payload.media_mimetype = imageFile.type;
      }

      const res = await createWhatsAppWebCampaign(payload);
      setFeedback({
        type: "success",
        text:
          payload.scheduled_at != null
            ? "✅ Campanha agendada!"
            : "✅ Campanha criada e está sendo processada!",
      });
      setMessage("");
      clearImage();
      clearPoll();
      setShowSchedule(false);
      setWhenMode("now");
    } catch (err) {
      setFeedback({ type: "danger", text: "Falha ao criar campanha." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <h2 className="mb-1">⚡ Quick Sender</h2>
          <p className="text-muted">Disparo único pra um segmento</p>

          {feedback && (
            <Alert variant={feedback.type} dismissible onClose={() => setFeedback(null)}>
              {feedback.text}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Card>
                <Card.Body>
                  <Form.Group>
                    <Form.Label>Segmento alvo *</Form.Label>
                    <Form.Control
                      as="select"
                      value={segmentId}
                      onChange={(e) => setSegmentId(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {segments.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.total_groups} grupos)
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Mensagem</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={poll ? `📊 Enquete: ${poll.question}` : message}
                      onChange={(e) => setMessage(e.target.value)}
                      disabled={!!poll}
                      placeholder="Digite o texto da campanha..."
                    />
                    <Form.Text className="text-muted">
                      Use <code>*negrito*</code>, <code>_itálico_</code>,{" "}
                      <code>~tachado~</code>
                    </Form.Text>
                  </Form.Group>

                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      label="@todos (menciona todos os membros do grupo)"
                      checked={mentionAll}
                      onChange={(e) => setMentionAll(e.target.checked)}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Anexo (opcional)</Form.Label>
                    {imagePreview ? (
                      <div className="d-flex align-items-start" style={{ gap: 8 }}>
                        <img
                          src={imagePreview}
                          alt="preview"
                          style={{
                            maxWidth: 120,
                            maxHeight: 120,
                            borderRadius: 8,
                          }}
                        />
                        <Button size="sm" variant="outline-danger" onClick={clearImage}>
                          <FaTrash />
                        </Button>
                      </div>
                    ) : (
                      <InputGroup>
                        <Form.File
                          accept="image/*"
                          onChange={handlePickImage}
                          disabled={!!poll}
                        />
                      </InputGroup>
                    )}
                  </Form.Group>

                  <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                    {!poll && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPoll(true)}
                      >
                        <FaPoll /> Criar enquete
                      </Button>
                    )}
                    {poll && (
                      <>
                        <Badge variant="secondary" className="p-2">
                          📊 {poll.options.length} opção(ões){" "}
                          {poll.allowMultipleAnswers ? "(múltipla)" : "(única)"}
                        </Badge>
                        <Button size="sm" variant="outline-danger" onClick={clearPoll}>
                          Remover enquete
                        </Button>
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Body>
                  <h5 className="mb-3">📅 Quando enviar?</h5>
                  <Form.Check
                    type="radio"
                    label="🔥 Enviar Agora"
                    checked={whenMode === "now"}
                    onChange={() => setWhenMode("now")}
                  />
                  <Form.Check
                    type="radio"
                    label="📅 Agendar"
                    checked={whenMode === "schedule"}
                    onChange={() => setWhenMode("schedule")}
                  />
                  {whenMode === "schedule" && (
                    <Row className="mt-2">
                      <Col>
                        <Form.Label>Data</Form.Label>
                        <Form.Control
                          type="date"
                          value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                        />
                      </Col>
                      <Col>
                        <Form.Label>Horário</Form.Label>
                        <Form.Control
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                        />
                      </Col>
                    </Row>
                  )}

                  <hr />
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={handleSend}
                    disabled={busy}
                  >
                    {busy ? <Spinner size="sm" animation="border" /> : <FaPaperPlane />}{" "}
                    {busy ? "Enviando..." : "Enviar"}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Modal Criar Enquete */}
      <Modal show={showPoll} onHide={() => setShowPoll(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>📊 Criar Enquete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Pergunta *</Form.Label>
            <Form.Control
              value={pollQuestion}
              onChange={(e) => setPollQuestion(e.target.value)}
            />
          </Form.Group>
          <Form.Label>Opções * (mín 2, máx 12)</Form.Label>
          {pollOptions.map((opt, idx) => (
            <InputGroup key={idx} className="mb-2">
              <Form.Control
                value={opt}
                placeholder={`Opção ${idx + 1}`}
                onChange={(e) => {
                  const next = [...pollOptions];
                  next[idx] = e.target.value;
                  setPollOptions(next);
                }}
              />
              {pollOptions.length > 2 && (
                <InputGroup.Append>
                  <Button
                    variant="outline-danger"
                    onClick={() =>
                      setPollOptions(pollOptions.filter((_, i) => i !== idx))
                    }
                  >
                    <FaTrash />
                  </Button>
                </InputGroup.Append>
              )}
            </InputGroup>
          ))}
          {pollOptions.length < 12 && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setPollOptions([...pollOptions, ""])}
            >
              + Adicionar opção
            </Button>
          )}
          <Form.Check
            className="mt-3"
            type="checkbox"
            label="Permitir múltiplas escolhas"
            checked={pollAllowMulti}
            onChange={(e) => setPollAllowMulti(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPoll(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={savePoll}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
